/**
 * Alyssa Edits v1 — Per-user × per-company signatures + sender resolution.
 *
 * Default sender = Shane Deason (~99% of mail). Alyssa or anyone else can swap
 * "Sending as" at send time without changing the active company persona. The
 * signature swaps live based on (selected user × active partner).
 */
import { supabase } from './supabase';
import { GENSERV_SIGNATURE_PARTNER_ID, buildGenservSignature, scriptureFrom } from './genservSignatureBlock';
import { markSignature, applySignature, withoutSignatureBlock } from './signatureBlock';

export const DEFAULT_SENDER_EMAIL = 'shane.deason@wellrepd.com';

/** Pick the default sender — Shane if active, otherwise the first active user. */
export function resolveDefaultSender(users, currentUser) {
  const list = Array.isArray(users) ? users.filter((u) => u.is_active) : [];
  if (list.length === 0) return null;
  const shane = list.find((u) => (u.email || '').toLowerCase() === DEFAULT_SENDER_EMAIL);
  if (shane) return shane;
  if (currentUser?.profile) {
    const me = list.find((u) => u.id === currentUser.profile.id);
    if (me) return me;
  }
  return list[0];
}

/** Fetch every active user_profile so we can populate the "Sending as" dropdown. */
export async function fetchActiveUsers() {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, email, role, is_active')
    .eq('is_active', true)
    .order('full_name');
  if (error) { console.error('[fetchActiveUsers]', error); return []; }
  return data || [];
}

// v1.7.19 — Shane is the canonical sender: everyone (incl. Alyssa) acts AS Shane today, so
// if the acting user has no signature for this partner, fall back to SHANE's signature for
// this partner before the (usually empty) partner default. This is why Phoenix appeared to
// have "no signature" — it loaded whichever user was acting, and only Shane's row was filled.
async function getSignatureRow(userId, partnerId) {
  if (!userId || !partnerId) return '';
  const { data } = await supabase
    .from('user_email_signatures')
    .select('signature')
    .eq('user_id', userId)
    .eq('partner_id', partnerId)
    .maybeSingle();
  return (data && data.signature) ? data.signature : '';
}

async function getShaneUserId() {
  const { data } = await supabase
    .from('user_profiles')
    .select('id')
    .ilike('email', DEFAULT_SENDER_EMAIL)
    .maybeSingle();
  return data?.id || null;
}

/** Get the signature for a given (user_id, partner_id) combo.
 *  Resolution order: the user's own signature → Shane's signature (canonical sender) → partner default. */
export async function getSignature(userId, partnerId, partnerFallback) {
  if (!partnerId) return partnerFallback || '';
  // GenServ always signs with the GenServ block (scripture, name, phone, wordmark; no Well Repd address), whoever is sending: the same builder the server and the Outbox use.
  if (partnerId === GENSERV_SIGNATURE_PARTNER_ID) {
    const closing = await getClosingBlock(null).catch(() => '');
    return buildGenservSignature({ scripture: scriptureFrom(closing) });
  }
  // 1) The acting user's own signature.
  if (userId) {
    const own = await getSignatureRow(userId, partnerId);
    if (own) return own;
  }
  // 2) Fall back to Shane's signature for this partner (everyone acts as Shane today).
  const shaneId = await getShaneUserId();
  if (shaneId && shaneId !== userId) {
    const shane = await getSignatureRow(shaneId, partnerId);
    if (shane) return shane;
  }
  // 3) Partner-level default.
  return partnerFallback || '';
}

// #2673 (Shane: "all emails should be signed Shane not Alyssa regardless of user"): what Compose, sequences and every outbound preview sign with. Same value as the server's
// OUTBOUND_SENDER_NAME in api/_lib/mergeEngine.js. getSignature(userId, ...) stays for the Settings editors that show or edit one person's own row; nothing outbound uses it with a user id.
export const OUTBOUND_SENDER_NAME = 'Shane Deason';
export function getOutboundSignature(partnerId, partnerFallback) {
  return getSignature(null, partnerId, partnerFallback);
}

/** Upsert a signature for (user × partner). */
export async function saveSignature(userId, partnerId, signature) {
  if (!userId || !partnerId) throw new Error('user and partner required');
  const { data, error } = await supabase
    .from('user_email_signatures')
    .upsert({
      user_id: userId,
      partner_id: partnerId,
      signature: signature || '',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,partner_id' })
    .select()
    .single();
  if (error) throw error;
  knownSigCache = null;
  return data;
}

/** Build the HTML body w/ the signature appended (compose default). */
// A loaded body ends with its sign-off; whatever blank paragraphs or line breaks trail it are dropped, and exactly one blank line separates the sign-off from the signature block (a template
// that carried its own trailing blanks used to stack up to about eight empty lines). An empty body keeps two blank paragraphs: room to start typing.
const TRAILING_BLANK = /(?:\s|<p[^>]*>\s*(?:<br\s*\/?>|&nbsp;| |\s)*<\/p>|<div[^>]*>\s*(?:<br\s*\/?>|&nbsp;| |\s)*<\/div>|<br\s*\/?>)+$/i;
const TRAILING_BR_IN_LAST = /(?:<br\s*\/?>\s*)+(<\/(?:p|div)>)\s*$/i;
export function trimTrailingBlank(html) {
  let out = String(html || '');
  for (let i = 0; i < 4; i += 1) { // stripping one kind can expose another
    const next = out.replace(TRAILING_BLANK, '').replace(TRAILING_BR_IN_LAST, '$1');
    if (next === out) break;
    out = next;
  }
  return out;
}
export function wrapWithSignature(body, signature) {
  const marked = signature ? markSignature(signature) : '';
  if (!body || !String(body).trim()) return marked ? `<p><br></p><p><br></p>${marked}` : '';
  const trimmed = trimTrailingBlank(body);
  return marked ? `${trimmed}<p><br></p>${marked}` : trimmed;
}

/**
 * v1.7.39.178 — Real Closing Block: single source of truth, unilateral
 * across every partner, matching {{sender.signature}}'s pattern but for
 * the "Regards, / Shane / Romans 12:11" block.
 *
 * #1448 (Shane, 2026-08-06/07, confirmed 2026-08-18): Alyssa gets her own
 * voice/verse for Travel In Faith -- supersedes the 2026-08-02 "never
 * varies per-partner" rule for this specific case. Real resolution
 * order: a real per-partner override row if one exists for the given
 * partnerId, else the real universal default (partner_id IS NULL) --
 * Shane's Romans 12:11 block, what every partner without a real
 * override still gets. Calling with no partnerId resolves to the same
 * universal default as before this change -- real, backward compatible.
 */
export async function getClosingBlock(partnerId = null) {
  if (partnerId) {
    const { data: override } = await supabase
      .from('closing_block')
      .select('content')
      .eq('partner_id', partnerId)
      .maybeSingle();
    if (override && override.content) return override.content;
  }
  const { data } = await supabase
    .from('closing_block')
    .select('content')
    .is('partner_id', null)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data && data.content) ? data.content : '';
}

// #1448 -- partnerId null = save the real universal default row (Shane's
// block, unchanged real behavior). partnerId set = save/create that
// partner's real override row, entirely separate from the default.
export async function saveClosingBlock(content, userId, partnerId = null) {
  let existingQuery = supabase.from('closing_block').select('id');
  existingQuery = partnerId ? existingQuery.eq('partner_id', partnerId) : existingQuery.is('partner_id', null);
  const { data: existing } = await existingQuery
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (existing) {
    const { data, error } = await supabase
      .from('closing_block')
      .update({ content: content || '', updated_at: new Date().toISOString(), updated_by: userId || null })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase
    .from('closing_block')
    .insert({ content: content || '', updated_by: userId || null, partner_id: partnerId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// #1448 -- real "revert to default" action. Only ever deletes a real
// per-partner override row; never touches the universal default
// (partnerId is required and non-null, guarded below).
export async function deleteClosingBlockOverride(partnerId) {
  if (!partnerId) return;
  const { error } = await supabase.from('closing_block').delete().eq('partner_id', partnerId);
  if (error) throw error;
}

// v1.7.16 — Shared headshot for signatures (hosted in Supabase partner-assets, public URL,
// renders inline in Outlook/Gmail — no attachment, no broken box). Same face across every
// persona; brand elements (color, logo, title) change per partner (Amenities Rule).
export const SIGNATURE_HEADSHOT_URL =
  'https://kmnrofueawqeisiclnpj.supabase.co/storage/v1/object/public/partner-assets/signatures/shane-headshot.jpg';

/**
 * buildSignatureHtml — the UNIVERSAL signature template.
 * Layout: round headshot (left) | brand-color divider | name + title + contact (right),
 * with the partner logo beneath the contact block. Table-based for email-client reliability.
 *
 * opts: {
 *   name, title, phone, email, website,   // person + role details
 *   brandColor, logoUrl, partnerName,      // per-persona brand elements
 *   photoUrl                               // defaults to the shared headshot
 * }
 */
export function buildSignatureHtml(opts = {}) {
  const {
    name = 'Shane Deason',
    title = '',
    phone = '',
    email = '',
    website = '',
    brandColor = '#0077cc',
    logoUrl = '',
    partnerName = '',
    photoUrl = SIGNATURE_HEADSHOT_URL,
  } = opts;

  const line = (label, value, href) => {
    if (!value) return '';
    const inner = href ? `<a href="${href}" style="color:#475569;text-decoration:none">${value}</a>` : value;
    return `<div style="font-size:12px;line-height:1.5;color:#475569">${inner}</div>`;
  };

  const contactRows =
    line('phone', phone, `tel:${String(phone).replace(/[^0-9+]/g, '')}`) +
    line('email', email, `mailto:${email}`) +
    line('web', website, /^https?:/.test(website) ? website : `https://${website}`);

  const logoRow = logoUrl
    ? `<div style="margin-top:6px"><img src="${logoUrl}" alt="${partnerName}" width="150" style="width:150px;max-width:150px;height:auto;display:block" /></div>`
    : (partnerName ? `<div style="margin-top:6px;font-size:12px;font-weight:700;color:${brandColor}">${partnerName}</div>` : '');

  // Inline-styled table so it survives Outlook/Gmail rendering.
  return (
    `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;border-collapse:collapse">` +
      `<tr>` +
        `<td style="vertical-align:top;padding-right:14px">` +
          `<img src="${photoUrl}" alt="${name}" width="72" height="72" style="width:72px;height:72px;border-radius:50%;display:block;object-fit:cover" />` +
        `</td>` +
        `<td style="vertical-align:top;border-left:3px solid ${brandColor};padding-left:14px">` +
          `<div style="font-size:15px;font-weight:700;color:#1a2133;line-height:1.3">${name}</div>` +
          (title ? `<div style="font-size:12px;font-weight:600;color:${brandColor};line-height:1.4;margin-bottom:4px">${title}</div>` : '') +
          contactRows +
          logoRow +
        `</td>` +
      `</tr>` +
    `</table>`
  );
}

// ── Signature swapping (Compose) ──
// Every non-empty signature in the system (all users x partners, plus the partner defaults), longest first. Cached for the session.
let knownSigCache = null;
export async function fetchKnownSignatures() {
  if (knownSigCache) return knownSigCache;
  try {
    const [{ data: rows }, { data: partners }] = await Promise.all([
      supabase.from('user_email_signatures').select('signature'),
      supabase.from('partners').select('email_signature'),
    ]);
    const all = [...(rows || []).map((r) => r.signature), ...(partners || []).map((p) => p.email_signature)].filter((s) => s && s.trim());
    knownSigCache = [...new Set(all)].sort((a, b) => b.length - a.length);
  } catch (_) { knownSigCache = []; }
  return knownSigCache;
}
export const resetKnownSignatures = () => { knownSigCache = null; };

// Put `sig` in the body in place of the signature it carries (see signatureBlock.js: found by the app's own marker, not by matching text).
export const swapSignature = (body, sig, known = []) => applySignature(body, sig, known);

const norm = (h) => String(h || '').replace(/>\s+</g, '><').replace(/\s+/g, ' ').trim();

// The body without any of the given signatures (to tell whether the person wrote anything besides the signature).
export function stripSignatures(body, sigs = []) {
  let out = withoutSignatureBlock(body);
  for (const k of sigs.filter(Boolean)) { out = out.split(k).join(''); const nk = norm(k); if (nk && norm(out).includes(nk)) out = norm(out).split(nk).join(''); }
  return out;
}

// Which partners have a stored, non-empty outbound signature: Shane's, whoever is sending (#2673; userId is kept for the callers and ignored). A Set of partner ids.
export async function fetchSignatureOwners(userId) { // eslint-disable-line no-unused-vars
  try {
    const shaneId = await getShaneUserId();
    const ids = [shaneId].filter(Boolean);
    if (!ids.length) return new Set();
    const { data } = await supabase.from('user_email_signatures').select('partner_id, signature').in('user_id', ids);
    return new Set((data || []).filter((r) => r.signature && r.signature.trim()).map((r) => r.partner_id));
  } catch (_) { return new Set(); }
}
